/**
 * Always-on player portrait for the HUD — a small WebGL view of the survivor's
 * actual preset GLB, playing the standing idle under a slow turn.
 *
 * Differs from `NpcViewer` in three ways that matter:
 *  1. It renders the **real GLB** for the character's preset, not a procedural
 *     humanoid, so the frame shows the body the player sees in the world.
 *  2. Its context is **long-lived** (the HUD is always up, unlike a dialog), so
 *     it asks for `low-power` and holds exactly one extra WebGL context.
 *  3. The model comes from `load-model.ts`'s module-level cache, which hands
 *     out `.clone()`s that SHARE geometry and materials with every other clone
 *     of that URL — the settlement's own player model included. So `dispose()`
 *     here must detach the model and free only what this view created. Freeing
 *     the shared geometry would corrupt the next mount, which is the same trap
 *     `render/scene.ts`'s dispose() documents around `userData.sharedAsset`.
 *
 * Fails safe: no WebGL, or a GLB that will not load, leaves the portrait box
 * empty rather than breaking the HUD.
 *
 * **It animates because the rest pose is a T-pose.** This view drove no
 * animator until 2026-09-09, which left the survivor standing in the HUD with
 * their arms straight out — and, because the arm span in a T-pose is wider
 * than the body is tall, clipped out of the portrait's own frame (the framing
 * note on the camera below reasons about the model's HEIGHT, which is only the
 * binding constraint once the arms are down).
 *
 * **It uses the PLAYER animator, not the NPC one** (changed 2026-09-15 —
 * Janeks: *"The player model should show the current avatar model, with all
 * equipment ... so matches the player in game"*). `createNpcAnimator` gave the
 * right idle but knows nothing about equipment, so the HUD showed an unarmed
 * survivor while the one in the world carried a rifle. `createPlayerAnimator`
 * subscribes to `gameStateStore`'s equipment itself and attaches the weapon,
 * headgear and torso models to the same bones the world player uses — so the
 * two match BY CONSTRUCTION rather than by a second attach path kept in sync
 * by hand. `ui/character-preview.ts` (the paperdoll) already does exactly
 * this; it is the proven shape, not a new idea.
 *
 * Fed a permanently idle input: a portrait never locomotes.
 *
 * Costs nothing measurable in clips: `character-animations.ts`'s clip cache is
 * module-level and this needs the same idle set the world player already
 * loaded — just one more `AnimationMixer` over 20 bones, ticked at the
 * throttled rate below. The equipment models it attaches ARE new objects, but
 * `loadPropModel` clones per call, so the portrait can never steal the rifle
 * out of the world player's hand (an `Object3D` has exactly one parent).
 *
 * Safe to animate independently of the settlement's player model even though
 * both come from `load-model.ts`'s one cache entry: skinned roots are handed
 * out as `SkeletonUtils.clone()`s, which duplicate the bones. A plain
 * `Object3D.clone()` would share them, and this portrait would fight the
 * settlement for the same skeleton.
 */
import * as THREE from 'three';
import { CHARACTERS } from '../../character-select/characters';
import { loadCharacterModel } from '../../character-select/load-model';
import { gameStateStore } from '../../state/game-state';
import { createPlayerAnimator, type PlayerAnimator, type PlayerAnimInput } from '../render/character-animations';

const MODEL_HEIGHT = 1.8;

/** A portrait never moves, and the player has no dialog pose with themselves. */
const IDLE_INPUT: PlayerAnimInput = {
  moving: false, gait: 'walk', crouched: false, aiming: false, dialogOpen: false,
};

/**
 * Reduced motion still needs the idle POSE — freezing on frame 0 is what put
 * the arms in a T-pose to begin with, so honouring the preference by not
 * animating reproduces the defect for exactly the users who opted out of
 * motion. So: run the animator briefly, then stop and leave the settled pose
 * on screen. Long enough to cover the clips' async load plus the crossfade
 * into the first idle (0.25s), short enough that nobody reads it as motion.
 */
const REDUCED_MOTION_SETTLE_SECONDS = 2;

/**
 * ~18Hz. The portrait is a small, always-on second WebGL scene in a 3D game,
 * animating nothing but a slow `sin` turn — rendering it at display rate spends
 * real GPU time on a motion too gentle for the extra frames to be visible.
 * `powerPreference: 'low-power'` is a hint to the browser, not a frame cap.
 *
 * Throttled rather than hidden while a panel is up: the paperdoll and the
 * vendor/map overlays are all translucent, so the frame genuinely shows through.
 */
const RENDER_INTERVAL_MS = 55;

export class PlayerPortrait {
  private renderer: THREE.WebGLRenderer | null = null;
  private scene: THREE.Scene | null = null;
  private camera: THREE.PerspectiveCamera | null = null;
  private model: THREE.Object3D | null = null;
  private animator: PlayerAnimator | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private ro: ResizeObserver | null = null;
  private unsubStore: (() => void) | null = null;
  private raf = 0;
  private clock = 0;
  private lastTime = 0;
  private lastRender = 0;
  private disposed = false;
  private reducedMotionQuery: MediaQueryList | null = null;
  private readonly onReducedMotionChange = (): void => this.applyMotionPreference();

  /** Mount into `host` (the portrait box). Loads the GLB asynchronously. */
  mount(host: HTMLElement): void {
    let renderer: THREE.WebGLRenderer;
    try {
      const canvas = document.createElement('canvas');
      canvas.className = 'bv-portrait-canvas';
      renderer = new THREE.WebGLRenderer({
        canvas, antialias: true, alpha: true, powerPreference: 'low-power',
      });
    } catch {
      return; // no WebGL — the styled empty box is an acceptable fallback
    }
    renderer.setClearColor(0x000000, 0);
    this.renderer = renderer;
    this.canvas = renderer.domElement;
    host.appendChild(this.canvas);

    const scene = new THREE.Scene();
    this.scene = scene;

    // Bust framing, face on the centre of the box. The box is 68x92 px, so the
    // full-body shot used before 2026-10-14 made the face a few pixels wide
    // (and, on a retina display, the missing canvas size rule cropped it too).
    // These rigs are big-headed: the eyes sit near 1.2m on a 1.8m model, not
    // at 1.6m, so aiming at "head height" put the face in the bottom third.
    // At 32deg FOV and 2.4m the view spans ~1.38m: 0.51m..1.89m, which keeps
    // the crown (and any headgear) inside the frame with the eyes on the centre.
    const camera = new THREE.PerspectiveCamera(32, 1, 0.1, 20);
    camera.position.set(0, 1.22, 2.4);
    camera.lookAt(0, 1.2, 0);
    this.camera = camera;

    // Same warm-key/cool-fill mood as the settlement and the dialog viewer.
    scene.add(new THREE.HemisphereLight(0x9fb8c9, 0x140d08, 0.6));
    const key = new THREE.DirectionalLight(0xffb27a, 1.2);
    key.position.set(1.6, 2.4, 2.2);
    scene.add(key);
    const rim = new THREE.DirectionalLight(0x6d8fa0, 0.45);
    rim.position.set(-2, 1.4, -1.5);
    scene.add(rim);

    this.ro = new ResizeObserver(() => this.resize());
    this.ro.observe(host);
    this.resize();

    void this.loadModel();

    this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.reducedMotionQuery.addEventListener('change', this.onReducedMotionChange);
    this.applyMotionPreference();

    // Under reduced motion the loop stops as soon as the pose has settled, so
    // an equip after that would mutate the scene with no frame left to show
    // it — the portrait would silently go stale for exactly the users who
    // opted out of motion. Same fix, same reasoning, as character-preview.ts.
    this.unsubStore = gameStateStore.subscribe(() => {
      if (this.reducedMotionQuery?.matches) this.applyMotionPreference();
    });
  }

  private async loadModel(): Promise<void> {
    const { character } = gameStateStore.get();
    const presetId = character?.state === 'ACTIVE' ? character.presetId : undefined;
    const def = CHARACTERS.find((c) => c.id === presetId) ?? CHARACTERS[0];
    try {
      const model = await loadCharacterModel(def.modelUrl, MODEL_HEIGHT);
      // dispose() may have run while the GLB was in flight.
      if (this.disposed || !this.scene) return;
      model.rotation.y = -0.3;
      this.model = model;
      this.scene.add(model);
      // No-ops (returns NOOP_ANIMATOR) if the preset turns out unrigged,
      // which leaves the old T-pose behaviour rather than throwing in the HUD.
      this.animator = createPlayerAnimator(model);
      // The GLB usually arrives after applyMotionPreference() has already run.
      // Under reduced motion that call rendered its one frame and stopped, so
      // there is no loop left to settle the pose — restart it now that there
      // is something to animate.
      this.applyMotionPreference();
    } catch (err) {
      console.warn('[player-portrait] model failed to load', err);
    }
  }

  private resize(): void {
    if (!this.renderer || !this.camera || !this.canvas) return;
    const host = this.canvas.parentElement;
    const w = Math.max(1, host?.clientWidth ?? 1);
    const h = Math.max(1, host?.clientHeight ?? 1);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderFrame();
  }

  /**
   * Reduced motion: no turn, and the loop runs only long enough to settle the
   * idle pose (see REDUCED_MOTION_SETTLE_SECONDS) before stopping on a static
   * frame. Without a model there is nothing to settle, so it stays at one
   * frame — `loadModel` re-runs this once the GLB lands.
   */
  private applyMotionPreference(): void {
    cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.lastTime = 0;
    this.clock = 0;
    this.lastRender = 0; // so the first frame after a (re)start draws immediately
    if (this.reducedMotionQuery?.matches && !this.animator) {
      this.renderFrame();
      return;
    }
    const loop = (now: number): void => {
      this.raf = requestAnimationFrame(loop);
      this.tick(now);
    };
    this.raf = requestAnimationFrame(loop);
  }

  private tick(now: number): void {
    if (now - this.lastRender < RENDER_INTERVAL_MS) return;
    // dt spans the whole throttled interval, not one display frame, so the
    // turn runs at the same speed regardless of the cap or the refresh rate.
    const dt = this.lastTime ? Math.min((now - this.lastTime) / 1000, 0.2) : 0;
    this.lastTime = now;
    this.lastRender = now;
    this.clock += dt;
    this.animator?.update(dt, IDLE_INPUT);
    const reduced = this.reducedMotionQuery?.matches ?? false;
    if (this.model && !reduced) this.model.rotation.y = Math.sin(this.clock * 0.45) * 0.4 - 0.3;
    this.renderFrame();
    if (reduced && this.clock >= REDUCED_MOTION_SETTLE_SECONDS) {
      cancelAnimationFrame(this.raf);
      this.raf = 0;
    }
  }

  private renderFrame(): void {
    if (!this.renderer || !this.scene || !this.camera) return;
    this.renderer.render(this.scene, this.camera);
  }

  /** Fully release GPU + DOM + listeners. Safe to call twice. */
  dispose(): void {
    this.disposed = true;
    cancelAnimationFrame(this.raf);
    this.raf = 0;

    if (this.reducedMotionQuery) {
      this.reducedMotionQuery.removeEventListener('change', this.onReducedMotionChange);
      this.reducedMotionQuery = null;
    }
    this.unsubStore?.();
    this.unsubStore = null;
    this.ro?.disconnect();
    this.ro = null;

    // Before detaching the model: the animator holds a mixer bound to its
    // bones, and uncaches that root on dispose.
    this.animator?.dispose();
    this.animator = null;

    // Detach only. The clone's geometry/materials are the very objects
    // load-model.ts's cache (and the settlement's own player model) hold —
    // disposing them here would corrupt the next mount. See class docstring.
    this.model?.removeFromParent();
    this.model = null;

    // Lights carry nothing disposable; clearing the scene is enough.
    this.scene?.clear();
    this.scene = null;
    this.camera = null;

    if (this.renderer) {
      this.renderer.dispose();
      this.renderer.forceContextLoss();
      this.renderer = null;
    }
    this.canvas?.remove();
    this.canvas = null;
    this.clock = 0;
    this.lastTime = 0;
    this.lastRender = 0;
  }
}
